/**
 * Closing a meeting briefing – the panel in the aside of the review detail
 * page. After the meeting (phase `held`) it leads the aside: a few lines on
 * the outcome and next steps, then "Close with debrief" or a plain "Close".
 * Before the meeting, closing means "not needed" and stays folded at the
 * bottom.
 *
 * Owns its text, persisted per briefing (`useDraft`), so a keystroke does not
 * re-render the document; the page owns the confirm, the close and the
 * navigation. Mount it keyed on the briefing.
 */
import { useId } from "react";

import { draftKey, useDraft } from "../../lib/hooks/useDraft";

export function BriefingClose({
  reviewId,
  held,
  author,
  pending,
  onClose,
}: {
  reviewId: string;
  /** The meeting is over – the debrief is open. */
  held: boolean;
  author: string;
  pending: boolean;
  /** True once closed, false when the confirm was declined; rejects on a
   * failed close (the page reports it, the text stays for a retry). */
  onClose: (notes: string) => Promise<boolean>;
}) {
  const id = useId();
  const { draft, setDraft, clearDraft } = useDraft(draftKey("briefing-debrief", reviewId));
  const close = async (notes: string) => {
    if (pending) return;
    try {
      if (await onClose(notes)) clearDraft();
    } catch {
      // Reported by the page's toast; the text stays.
    }
  };

  if (held) {
    return (
      <section className="mb-6 pb-5 border-b border-rule" aria-labelledby={`${id}-heading`}>
        <h2 id={`${id}-heading`} className="font-heading font-semibold mb-2">Debrief</h2>
        <p className="text-xs text-ink-low mb-3">
          The meeting is over. A few lines on the outcome and next steps – {author} follows up on them.
        </p>
        <textarea
          aria-labelledby={`${id}-heading`}
          className="input w-full"
          rows={6}
          maxLength={20000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={pending}
          placeholder="Outcome, agreements, next steps…"
        />
        <div className="flex gap-2 flex-wrap mt-2">
          <button
            type="button"
            className="btn btn-sm btn-main"
            disabled={pending || !draft.trim()}
            onClick={() => void close(draft)}
          >
            Close with debrief
          </button>
          <button type="button" className="btn btn-sm btn-ghost" disabled={pending} onClick={() => void close("")}>
            Close
          </button>
        </div>
        <p className="text-xs text-ink-low mt-2">Left untouched, it closes on its own 3 days after the meeting.</p>
      </section>
    );
  }

  return (
    <details className="mt-6 pt-5 border-t border-rule">
      <summary className="text-sm text-ink-medium cursor-pointer">Close – not needed</summary>
      <p className="text-xs text-ink-low mt-2 mb-2">
        Closing before the meeting tells {author} the briefing is not needed, for example because the meeting was cancelled.
      </p>
      <textarea
        aria-label="Note for the author (optional)"
        className="input w-full"
        rows={3}
        maxLength={20000}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={pending}
        placeholder="Why it is not needed (optional)"
      />
      <button type="button" className="btn btn-sm btn-second mt-2" disabled={pending} onClick={() => void close(draft)}>
        Close
      </button>
    </details>
  );
}
